import React from 'react';
import styled, { css } from 'styled-components';
import {
  FlowTemplates as FlowTemplatesType,
  useGetFlowV2TemplatesQuery,
} from '~/graphql/types';
import { isEmpty, sort } from 'ramda';
import useCurrentAccount from '~/hooks/useCurrentAccount';

import { LINK_PREFIX } from '../Wizard';
import TemplateDetails from './components/TemplateDetails';
import groupByCategory from './utils/groupByCategory';
import useErrorReporter from '~/hooks/useErrorReporter';
import Loading from '~/components/atom/Loading';
import AppErrorScreen from '~/components/template/AppErrorScreen';
import MasterDetail from '~/components/template/MasterDetail';
import usePermissions from '~/hooks/usePermissions';

type Template = FlowTemplatesType & { weight?: number };
export type FlowTemplatesCategory = {
  [key: string]: {
    name: string;
    slug: string;
    templates: Array<Template>;
  };
};

export type Props = {
  onSelect: (template: FlowTemplatesType | null) => void;
  template?: FlowTemplatesType | null;
};

export const MOST_USED_CATEGORY_SLUG = 'meest-gebruikt';
export const MOST_USED_CATEGORY_LABEL = 'Meest gebruikt';
export const EMPTY_TEMPLATE_ID = 'empty-flow-template';

const FlowTemplates: React.FCC<Props> = ({ onSelect, template }) => {
  const flowsPermission = usePermissions(['root.automation']).allowed;

  const account = useCurrentAccount();
  const reporter = useErrorReporter();
  const {
    loading: loadingV2,
    error: errorV2,
    data: dataV2,
  } = useGetFlowV2TemplatesQuery({
    variables: { accountId: account.id },
    skip: flowsPermission === false,
  });

  const queryValues = {
    data: dataV2,
    loading: loadingV2,
    error: errorV2,
    templates: dataV2?.getFlowV2Templates,
  };

  const flowTemplates: Array<Template> = [
    {
      __typename: 'FlowTemplates' as const,
      id: EMPTY_TEMPLATE_ID,
      token: EMPTY_TEMPLATE_ID,
      name: 'Nieuwe flow',
      description: 'Bouw je eigen flow',
      categories: [MOST_USED_CATEGORY_LABEL],
      weight: 9999,
      appTypes: [],
    },
    ...(queryValues.templates ?? []),
    // Guard against empty categories
  ].filter(({ categories }) => categories.length !== 0);

  const groupedTemplates: FlowTemplatesCategory | undefined =
    groupByCategory(flowTemplates);

  if (queryValues.loading)
    return (
      <Container>
        <Loading />
      </Container>
    );
  if (
    !queryValues.data ||
    !groupedTemplates ||
    isEmpty(groupedTemplates) ||
    queryValues.error
  ) {
    reporter.captureException(
      queryValues.error ?? new Error('Could not get flow templates'),
      'fatal',
    );

    return <AppErrorScreen />;
  }

  const categories = Object.keys(groupedTemplates);
  const sortedCategories = sort((a, b) => a.localeCompare(b), categories);

  const version = '/v2';

  return (
    <Container>
      <StyledMasterDetail
        basePath={LINK_PREFIX}
        navbar={[
          {
            type: 'link',
            // To select the first group by default
            to: `${version}/`,
            name: MOST_USED_CATEGORY_LABEL,
          },
          ...sortedCategories
            .filter(slug => slug !== MOST_USED_CATEGORY_SLUG)
            .map(slug => ({
              type: 'link' as const,
              // To select the first group by default
              to: `${version}/${slug}`,
              name: groupedTemplates[slug].name,
            })),
        ]}
      >
        <TemplateDetails
          default
          path={`/${EMPTY_TEMPLATE_ID}`}
          flowCategories={groupedTemplates}
          onSelect={onSelect}
          templateId={template?.id ?? null}
        />
        <TemplateDetails
          path=":slug"
          flowCategories={groupedTemplates}
          onSelect={onSelect}
          templateId={template?.id ?? null}
        />
      </StyledMasterDetail>
    </Container>
  );
};

const StyledMasterDetail = styled(MasterDetail)(
  ({ theme }) => css`
    min-width: 200px;
    width: 100%;

    border-right: 1px solid ${theme.color('tertiary')};
    margin-right: ${theme.space('l')};
  `,
);

const Container = styled.div(
  ({ theme }) => css`
    width: 100%;
    margin: ${theme.space('l')} 0;
  `,
);

export default FlowTemplates;
